import type { ReactNode } from "react";
import type {
  CellProps as ReactAriaCellProps,
  ColumnProps as ReactAriaColumnProps,
  RowProps as ReactAriaRowProps,
  TableBodyProps as ReactAriaTableBodyProps,
  TableProps as ReactAriaTableProps,
} from "react-aria-components";
import {
  Cell as ReactAriaCell,
  Column as ReactAriaColumn,
  Row as ReactAriaRow,
  Table as ReactAriaTable,
  TableBody as ReactAriaTableBody,
  TableHeader as ReactAriaTableHeader,
} from "react-aria-components";

import { cn } from "../utils";
import { NcButton } from "./nc-button";
import { NcIconProps, NcIconSortAscending, NcIconSortDescending, NcIconSortNone } from "./nc-icons";

interface NcTableProps extends ReactAriaTableProps {
  styleRowHeaders?: boolean;
}

const NcTable = ({ styleRowHeaders, className, ...props }: NcTableProps) => (
  <ReactAriaTable
    data-nc="NcTable"
    className={cn(
      "border-ui data-focus-visible:outline-main w-full border data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
      styleRowHeaders && "[&_td[role='rowheader']]:bg-ui-light",
      className
    )}
    {...props}
  />
);

const TableHeader = ({ ...props }) => (
  <ReactAriaTableHeader data-nc="NcTable.TableHeader" {...props} />
);

interface ColumnProps extends ReactAriaColumnProps {
  children: ReactNode;
}

interface SortIconProps extends NcIconProps {
  sortDirection?: "ascending" | "descending";
}

const SortIcon = ({ sortDirection, ...props }: SortIconProps) => {
  switch (sortDirection) {
    case "ascending":
      return <NcIconSortAscending {...props} />;
    case "descending":
      return <NcIconSortDescending {...props} />;
    default:
      return <NcIconSortNone {...props} />;
  }
};

const Column = ({ children, className, ...props }: ColumnProps) => (
  <ReactAriaColumn
    data-nc="NcTable.Column"
    className={cn(
      "bg-ui-light border-ui data-focus-visible:outline-main border border-t-0 p-2 text-left font-bold first-of-type:border-l-0 last-of-type:border-r-0 data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
      className
    )}
    {...props}
  >
    {({ sort, allowsSorting, sortDirection }) => (
      <>
        {allowsSorting ? (
          <>
            <NcButton
              className="w-full px-2"
              onPress={() => {
                if (!sortDirection) {
                  sort("ascending");
                  return;
                }
                sort(sortDirection === "ascending" ? "descending" : "ascending");
              }}
            >
              {children}
              <SortIcon sortDirection={sortDirection} className="ml-auto" />
            </NcButton>
          </>
        ) : (
          <>{children}</>
        )}
      </>
    )}
  </ReactAriaColumn>
);

type NcTableBodyProps<T> = ReactAriaTableBodyProps<T>;

const TableBody = <T extends object>({ className, ...props }: NcTableBodyProps<T>) => (
  <ReactAriaTableBody
    data-nc="NcTable.TableBody"
    className={cn(
      "[&_tr:first-of-type_td]:border-t-0 [&_tr:first-of-type_th]:border-t-0 [&_tr:last-of-type_td]:border-b-0 [&_tr:last-of-type_th]:border-b-0",
      "[&[data-empty='true']_td]:p-4 [&[data-empty='true']_td]:text-center [&[data-empty='true']_td]:italic",
      className
    )}
    {...props}
  />
);

type NcTableRowProps<T> = ReactAriaRowProps<T>;

const Row = <T extends object>({ className, ...props }: NcTableRowProps<T>) => (
  <ReactAriaRow
    data-nc="NcTable.Row"
    className={cn(
      "data-hovered:bg-focused data-focused:bg-focused data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-href:cursor-pointer",
      className
    )}
    {...props}
  />
);

type NcTableCellProps = ReactAriaCellProps;

const Cell = ({ className, ...props }: NcTableCellProps) => (
  <ReactAriaCell
    data-nc="NcTable.Cell"
    className={cn(
      "border-ui data-focus-visible:outline-main border p-2 first-of-type:border-l-0 last-of-type:border-r-0 data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
      className
    )}
    {...props}
  />
);

NcTable.TableHeader = TableHeader;
NcTable.Column = Column;
NcTable.TableBody = TableBody;
NcTable.Row = Row;
NcTable.Cell = Cell;

export { NcTable };
